import React, { useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';

import Typography from '@mui/material/Typography';

import type { GridColDef } from '@p81-common/ui';
import { CustomIcon, TableTextGroup, _palette } from '@p81-common/ui';
import { Member } from 'components/common/Elements/Elements';
import {
  defaultFilters as featureListFilters,
  defaultNavigation as featureListNavigation
} from 'components/Product/FeaturesFlag/FeatureList/helpers/configurates';

import { useRegionalLink } from 'hooks/useRegionalLink';
import { getInitialsFull } from 'utils/common';
import { links } from 'data/constants';

import type { ActivityLogRow, FeatureFlagRow } from './types';

const EMPTY = '—';

const Dash = ({ caption }: { caption?: string }) => (
  <TableTextGroup title={EMPTY} subtitle={caption || ''} />
);

const ActionsCell = ({
  row,
  canWrite,
  onUnassign
}: {
  row: FeatureFlagRow;
  canWrite: boolean;
  onUnassign: (row: FeatureFlagRow) => void;
}) => {
  const history = useHistory();
  const generateRegionalPath = useRegionalLink();
  const [menuOpen, setMenuOpen] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);

  const closeMenu = () => setMenuOpen(false);

  // A GA flag has no per-tenant binding to remove, and read-only users cannot
  // write. Both stay visible but disabled so the rule reads as a rule.
  const isGlobal = row.state === 'globalAssigned';
  const unassignDisabled = isGlobal || !canWrite;
  const unassignReason = isGlobal
    ? 'This flag is on for every tenant, so it cannot be unassigned here'
    : 'You do not have permission to change feature flags';

  const handleUnassign = () => {
    onUnassign(row);
    closeMenu();
  };

  // The global list the sidebar labels "Feature Flag Rollout" — same page.
  const handleViewInFeatureList = () => {
    // FeatureList reads `filter`, `changeFilter` and `jarvisRegion` only — a
    // bare ?search= was silently ignored. It also REPLACES its whole filter
    // object from the param (FeatureList.tsx:286), so the defaults have to
    // travel with it or the page requests page=undefined.
    const listFilter = encodeURIComponent(
      JSON.stringify({
        ...featureListFilters,
        ...featureListNavigation,
        search: row.name
      })
    );

    history.push(
      generateRegionalPath(`/product/feature-flag?filter=${listFilter}`)
    );
    closeMenu();
  };

  const unassignItem = (
    <MenuItem onClick={handleUnassign} disabled={unassignDisabled}>
      <ListItemText>Unassign</ListItemText>
    </MenuItem>
  );

  return (
    <div>
      <IconButton
        ref={ref}
        onClick={() => setMenuOpen(!menuOpen)}
        size="small"
        sx={{
          backgroundColor: 'white',
          '&:hover': { backgroundColor: 'white' }
        }}
      >
        <CustomIcon
          src={`${links.imagesBaseUrl}/icons/icon_3dots.svg`}
          style={{ transform: 'rotate(90deg)' }}
        />
      </IconButton>

      <Popover
        anchorEl={ref.current}
        open={menuOpen}
        onClose={closeMenu}
        disableScrollLock={false}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{
          sx: { '&::before': { left: 'calc(50% - 5px) !important' } }
        }}
      >
        <MenuList>
          {unassignDisabled ? (
            // MUI drops pointer events on a disabled MenuItem, so the tooltip
            // needs a wrapper that still receives them.
            <Tooltip title={unassignReason} placement="left">
              <span>{unassignItem}</span>
            </Tooltip>
          ) : (
            unassignItem
          )}
          <MenuItem onClick={handleViewInFeatureList}>
            <ListItemText>View in Feature List</ListItemText>
          </MenuItem>
        </MenuList>
      </Popover>
    </div>
  );
};

export const createFeatureFlagsConfig = ({
  canWrite,
  onUnassign
}: {
  canWrite: boolean;
  onUnassign: (row: FeatureFlagRow) => void;
}): GridColDef[] => [
  {
    show: true,
    isSortable: true,
    field: 'name',
    title: 'Feature Name',
    width: 300,
    type: 'textList',
    fieldsToExport: ['name', 'ffKey'],
    renderCell: ({ row }: { row: FeatureFlagRow }) => (
      <TableTextGroup title={row.name} subtitle={row.ffKey} />
    )
  },
  {
    show: true,
    // rank is always the primary sort key and stateLabel is a pure function of
    // it, so a secondary sort here could never reorder anything.
    isSortable: false,
    field: 'stateLabel',
    title: 'Status',
    width: 200,
    type: 'textList',
    fieldsToExport: ['stateLabel']
  },
  {
    show: true,
    isSortable: false,
    field: 'description',
    title: 'Description',
    width: 220,
    type: 'textList',
    numOfLines: 2,
    fieldsToExport: ['description']
  },
  {
    show: true,
    isSortable: true,
    field: 'ownerName',
    title: 'Owner',
    width: 210,
    type: 'textList',
    fieldsToExport: ['ownerName', 'ownerEmail'],
    renderCell: ({ row }: { row: FeatureFlagRow }) =>
      row.ownerName ? (
        <TableTextGroup title={row.ownerName} subtitle={row.ownerEmail} />
      ) : (
        <Dash caption="No owner set" />
      )
  },
  {
    show: true,
    isSortable: false,
    field: 'assignedByName',
    title: 'Assigned By',
    width: 230,
    type: 'textList',
    fieldsToExport: ['assignedByName', 'assignedByEmail'],
    renderCell: ({ row }: { row: FeatureFlagRow }) => {
      // GA is not assigned to anyone in particular; below the assigned cluster
      // the column is genuinely empty.
      if (row.state === 'globalAssigned') return <Dash caption="All tenants" />;
      if (!row.assignedByName) return <Dash />;
      // Same shape as createMember in FeatureList/helpers/configurates.tsx.
      // No initialsColor: the repo Avatar hashes the initials into colorList,
      // which is what gives "Updated By" its colours on the global page.
      return (
        <Member
          member={{
            fullName: row.assignedByName,
            description: row.assignedByEmail || '',
            initials: getInitialsFull(row.assignedByName)
          }}
        />
      );
    }
  },
  {
    show: true,
    // Resolved by a $lookup inside the data branch of $facet, i.e. after the
    // server's $sort — so ascending and descending returned the same page.
    isSortable: false,
    field: 'assignedOn',
    title: 'Assigned On',
    width: 160,
    type: 'dateCreated',
    cellEmptyState: EMPTY,
    fieldsToExport: ['assignedOn']
  },
  {
    show: true,
    isSortable: false,
    field: 'actions',
    type: 'actionButtons',
    title: '',
    width: 54,
    renderCell: ({ row }: { row: FeatureFlagRow }) => (
      <ActionsCell row={row} canWrite={canWrite} onUnassign={onUnassign} />
    )
  }
];

// Nothing here is sortable: the logs endpoint accepts customerId and search
// only, and rows arrive newest-first. `sortingMode="server"` stays on the grid
// so MUI does not client-sort the visible page and imply more than that.
export const activityLogConfig: GridColDef[] = [
  {
    show: true,
    isSortable: false,
    field: 'date',
    title: 'Date',
    width: 180,
    type: 'dateCreated',
    fieldsToExport: ['date']
  },
  {
    show: true,
    isSortable: false,
    field: 'action',
    title: 'Action',
    width: 140,
    type: 'textList',
    fieldsToExport: ['action']
  },
  {
    show: true,
    isSortable: false,
    field: 'status',
    title: 'Result',
    width: 120,
    type: 'textList',
    fieldsToExport: ['status'],
    // A failed Assign used to render identically to a successful one, which is
    // the failure mode the Error-row fix set out to remove.
    renderCell: ({ row }: { row: ActivityLogRow }) => (
      <Typography
        fontSize={14}
        color={
          row.status === 'Error' ? _palette.error[500] : _palette.primary[500]
        }
      >
        {row.status === 'Error' ? 'Failed' : 'Success'}
      </Typography>
    )
  },
  {
    show: true,
    isSortable: false,
    field: 'featureName',
    title: 'Feature Name',
    width: 300,
    type: 'textList',
    fieldsToExport: ['featureName']
  },
  {
    show: true,
    isSortable: false,
    field: 'description',
    title: 'Description',
    width: 240,
    type: 'textList',
    numOfLines: 2,
    fieldsToExport: ['description']
  },
  {
    show: true,
    isSortable: false,
    field: 'updatedByName',
    title: 'Performed By',
    width: 260,
    type: 'textList',
    fieldsToExport: ['updatedByName', 'updatedByEmail'],
    renderCell: ({ row }: { row: { [k: string]: string } }) => (
      <TableTextGroup title={row.updatedByName} subtitle={row.updatedByEmail} />
    )
  }
];

// Seeds useDataGrid's `filter`. Pagination is NOT set here: the hook spreads
// `defaultNavigation` (currentPage 1, itemsPerPage 25) after this object, so
// anything named the same would be silently overridden. Search, status and
// owner are held as page state instead of going through the hook's filters.
export const defaultFilters = {};
